import React, { useEffect, useState, useCallback } from 'react';
import { apiCall } from '../lib/api';
import { cn } from '../lib/utils';
import { ConfirmDialog } from '../components/ConfirmDialog';

interface SourceSystem {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
}

interface FormData {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
}

const emptyForm: FormData = {
  id: '',
  name: '',
  description: '',
  enabled: true,
};

export const SourceSystemsPage: React.FC = () => {
  const [systems, setSystems] = useState<SourceSystem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const loadSystems = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await apiCall('/api/config/sources');
      if (resp.ok) {
        const data = await resp.json();
        setSystems(data.source_systems || []);
      }
    } catch {
      // Keep page usable; error details surface during save.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSystems(); }, [loadSystems]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
    setShowForm(true);
  };

  const openEdit = (system: SourceSystem) => {
    setEditingId(system.id);
    setForm({
      id: system.id,
      name: system.name,
      description: system.description || '',
      enabled: system.enabled,
    });
    setError(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const url = editingId ? `/api/config/sources/${editingId}` : '/api/config/sources';
      const method = editingId ? 'PUT' : 'POST';
      const body: any = {
        name: form.name,
        description: form.description,
        enabled: form.enabled,
      };
      if (!editingId) body.id = form.id;

      const resp = await apiCall(url, { method, body: JSON.stringify(body) });
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Failed to save source system');
      }

      setShowForm(false);
      await loadSystems();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const resp = await apiCall(`/api/config/sources/${deleteTarget}`, { method: 'DELETE' });
    setDeleteTarget(null);
    if (!resp.ok) {
      const data = await resp.json();
      setError(data.error || 'Failed to delete source system');
      return;
    }
    await loadSystems();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Source Systems</h1>
          <p className="text-sm text-gray-500 mt-1">Register and manage systems that can publish tenant events.</p>
        </div>
        <button onClick={openCreate} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
          Add Source
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold">{editingId ? `Edit: ${editingId}` : 'New Source System'}</h2>
          {error && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!editingId && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Source ID</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form.id}
                  onChange={e => setForm(f => ({ ...f, id: e.target.value }))}
                  placeholder="nova"
                />
                <p className="text-xs text-gray-400 mt-1">Use lowercase letters, numbers, hyphen, or underscore.</p>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Wetsens Nova"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Where these tenant events come from"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
              />
              Enabled
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 font-semibold text-gray-600">ID</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Name</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Description</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Status</th>
                <th className="text-right px-5 py-3 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {systems.map(s => (
                <tr key={s.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-5 py-3 font-mono text-gray-700">{s.id}</td>
                  <td className="px-5 py-3 font-medium text-gray-900">{s.name}</td>
                  <td className="px-5 py-3 text-gray-500">{s.description || '--'}</td>
                  <td className="px-5 py-3">
                    <span className={cn(
                      'text-xs font-medium px-2 py-0.5 rounded',
                      s.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
                    )}>
                      {s.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right space-x-2">
                    <button onClick={() => openEdit(s)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Edit</button>
                    <button onClick={() => setDeleteTarget(s.id)} className="text-red-600 hover:text-red-800 text-xs font-medium">Delete</button>
                  </td>
                </tr>
              ))}
              {systems.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-400">No source systems registered. Add one before creating accounts or tenants.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Are you sure you want to delete this?"
        message={`This will permanently delete the source system "${deleteTarget}".`}
        warning="All accounts, tenants, and associated data linked to this source system will be permanently deleted. This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};
